/**
 * The table's sounds.
 *
 * Synthesised in the browser rather than loaded as files. Three reasons, and
 * the third is the one that decided it: no asset to download on a phone, no
 * external host for the page's own strict CSP to refuse, and a tone that can be
 * *derived* from what happened — a raise is the same shape as a call, pitched
 * up, which is why the two are told apart without anybody being taught.
 *
 * Sound in a poker client has one job: tell you an action happened when you are
 * not looking at the screen. So each one is short, quiet, and different enough
 * from its neighbours to be identified on its own. Fold is a soft thud with no
 * pitch to it; check is a knuckle on the table; call is a single clean note;
 * raise is that note plus a higher one; the pot coming your way is a small
 * ascending run.
 *
 * Nothing plays until the player has interacted with the page, because browsers
 * refuse to start audio before that — and a room that begins making noise the
 * instant it loads deserves to be refused anyway.
 */

export type Cue = 'deal' | 'check' | 'call' | 'bet' | 'raise' | 'fold' | 'allin' | 'win' | 'turn';

const STORAGE_KEY = 'pf-sound';

let context: AudioContext | null = null;
let master: GainNode | null = null;

function ready(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (context === null) {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return null;
    context = new Ctor();
    master = context.createGain();
    master.gain.value = 0.5;
    master.connect(context.destination);
  }
  // Browsers suspend the context until a gesture. Resuming is cheap and a no-op
  // once it has started.
  if (context.state === 'suspended') void context.resume();
  return context;
}

export function soundEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(STORAGE_KEY) !== 'off';
}

export function setSoundEnabled(on: boolean): void {
  localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  if (on) ready();
}

/**
 * One note.
 *
 * A short attack and an exponential decay, which is what makes a synthesised
 * tone sound like something being struck rather than a test tone being switched
 * on and off. The click at the end of a square wave is worse than no sound at
 * all, so every envelope ends at silence rather than being cut.
 */
function tone(
  at: number,
  options: {
    freq: number;
    duration: number;
    type?: OscillatorType;
    gain?: number;
    sweepTo?: number;
  },
): void {
  const ctx = context;
  if (ctx === null || master === null) return;

  const osc = ctx.createOscillator();
  const amp = ctx.createGain();
  osc.type = options.type ?? 'sine';
  osc.frequency.setValueAtTime(options.freq, at);
  if (options.sweepTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(options.sweepTo, at + options.duration);
  }

  const peak = options.gain ?? 0.16;
  amp.gain.setValueAtTime(0.0001, at);
  amp.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  amp.gain.exponentialRampToValueAtTime(0.0001, at + options.duration);

  osc.connect(amp);
  amp.connect(master);
  osc.start(at);
  osc.stop(at + options.duration + 0.02);
}

/**
 * A burst of noise, for the sounds that are not notes.
 *
 * A card landing and a knuckle on the table have no pitch. Trying to make them
 * out of a sine wave is what makes a poker client sound like a toy.
 */
function thud(at: number, options: { duration: number; gain?: number; cutoff?: number }): void {
  const ctx = context;
  if (ctx === null || master === null) return;

  const frames = Math.max(1, Math.floor(ctx.sampleRate * options.duration));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    // Decaying noise: loud at the strike, gone almost immediately.
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames) ** 3;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = options.cutoff ?? 900;
  const amp = ctx.createGain();
  amp.gain.value = options.gain ?? 0.22;

  source.connect(filter);
  filter.connect(amp);
  amp.connect(master);
  source.start(at);
}

export function play(cue: Cue): void {
  if (!soundEnabled()) return;
  const ctx = ready();
  if (ctx === null) return;
  const now = ctx.currentTime;

  switch (cue) {
    // A card sliding onto the felt. Noise, not a note.
    case 'deal':
      thud(now, { duration: 0.1, gain: 0.14, cutoff: 2600 });
      break;

    // Two knuckles on the table, which is what a check is.
    case 'check':
      thud(now, { duration: 0.07, gain: 0.2, cutoff: 500 });
      thud(now + 0.11, { duration: 0.07, gain: 0.16, cutoff: 500 });
      break;

    // Chips going in: one clean note.
    case 'call':
      tone(now, { freq: 520, duration: 0.16, type: 'triangle', gain: 0.14 });
      break;

    // A bet is a call with more behind it.
    case 'bet':
      tone(now, { freq: 520, duration: 0.14, type: 'triangle', gain: 0.14 });
      tone(now + 0.07, { freq: 660, duration: 0.18, type: 'triangle', gain: 0.13 });
      break;

    // A raise is the same shape, pitched up — told apart without being taught.
    case 'raise':
      tone(now, { freq: 620, duration: 0.13, type: 'triangle', gain: 0.15 });
      tone(now + 0.08, { freq: 830, duration: 0.2, type: 'triangle', gain: 0.14 });
      break;

    // Cards pushed away. Low, dull, and over.
    case 'fold':
      thud(now, { duration: 0.16, gain: 0.2, cutoff: 340 });
      break;

    // Everything, at once.
    case 'allin':
      tone(now, { freq: 440, duration: 0.5, type: 'sawtooth', gain: 0.1, sweepTo: 880 });
      thud(now, { duration: 0.2, gain: 0.18, cutoff: 1400 });
      break;

    // The pot coming your way.
    case 'win':
      tone(now, { freq: 523.25, duration: 0.18, type: 'sine', gain: 0.16 });
      tone(now + 0.12, { freq: 659.25, duration: 0.18, type: 'sine', gain: 0.16 });
      tone(now + 0.24, { freq: 783.99, duration: 0.34, type: 'sine', gain: 0.17 });
      break;

    // It is on you. Deliberately the most noticeable one, because it is the
    // only sound that is asking for something rather than reporting it.
    case 'turn':
      tone(now, { freq: 880, duration: 0.12, type: 'sine', gain: 0.13 });
      tone(now + 0.16, { freq: 1174.66, duration: 0.16, type: 'sine', gain: 0.12 });
      break;
  }
}
